import { resolveInvoiceLineItemsFromRecord } from "../../../../utilis/invoiceEditHelpers";
import { resolveLinkedInvoicePaidShare } from "../commissionInvoiceLineSuggestions";
import { formatCurrency } from "./commissionDetailConstants";

const FEE_META = {
  application_fee: { label: "Application fee" },
  material_fee: { label: "Material fee" },
};

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function resolveInquiryFeeType(line = {}) {
  const feeType = String(line?.fee_type ?? line?.feeType ?? "").toLowerCase();
  if (feeType === "application_fee" || feeType === "material_fee") {
    return feeType;
  }

  const description = String(
    line?.description ?? line?.item_name ?? line?.itemName ?? "",
  )
    .trim()
    .toLowerCase();

  if (description.includes("application fee")) return "application_fee";
  if (description.includes("material fee")) return "material_fee";
  return "";
}

function resolveLineAmount(line = {}) {
  if (Number(line?.commission_installment_id) || Number(line?.bonus_installment_id)) {
    return 0;
  }

  const lineTotal = Number(line?.line_total ?? line?.amount) || 0;
  if (lineTotal > 0) return roundMoney(lineTotal);

  const unitPrice = Number(line?.unit_price ?? line?.unitPrice) || 0;
  const qty = Number(line?.quantity ?? line?.qty) || 1;
  const discount = Number(line?.discount ?? line?.discount_amount) || 0;
  return roundMoney(Math.max(0, unitPrice * qty - discount));
}

function isActiveInvoiceStatus(status = "") {
  const normalized = String(status || "").toLowerCase();
  return !["cancelled", "void", "refunded"].includes(normalized);
}

function resolvePaymentStatusLabel({ lineAmount, linePaid, invoiceStatus }) {
  const amount = roundMoney(lineAmount);
  const paid = roundMoney(linePaid);
  const status = String(invoiceStatus || "").toLowerCase();

  if (amount <= 0) return "—";
  if (paid + 0.009 >= amount || status === "paid") return "Paid";
  if (paid > 0.009) return "Partial";
  if (status === "draft") return "Draft";
  if (status === "awaiting_approval") return "Awaiting approval";
  if (status === "awaiting_payment" || status === "partial") {
    return "Awaiting payment";
  }
  return "Unpaid";
}

function statusTone(label) {
  if (label === "Paid") return "green";
  if (label === "Partial" || label === "Awaiting payment") return "amber";
  if (label === "Draft") return "slate";
  return "slate";
}

function collectInquiryFeeInvoiceLines(invoices = []) {
  const rows = [];

  (Array.isArray(invoices) ? invoices : []).forEach((invoiceRow) => {
    const invoiceRecord = invoiceRow?.raw || invoiceRow?.wrapper || invoiceRow;
    const status = String(invoiceRow?.status ?? invoiceRecord?.status ?? "");
    if (!isActiveInvoiceStatus(status)) return;

    const invoiceNumber =
      invoiceRow?.invoiceNumber ||
      invoiceRecord?.invoice_number ||
      (invoiceRow?.id != null ? `INV-${invoiceRow.id}` : "—");

    const invoicePayload = {
      paid_amount:
        invoiceRow?.paidAmount ??
        invoiceRecord?.paid_amount ??
        invoiceRecord?.paidAmount,
      total_amount:
        invoiceRow?.amount ??
        invoiceRecord?.total_amount ??
        invoiceRecord?.totalAmount,
      status,
    };

    const lines = resolveInvoiceLineItemsFromRecord(invoiceRecord);
    lines.forEach((line) => {
      const feeType = resolveInquiryFeeType(line);
      if (!feeType) return;

      const amount = resolveLineAmount(line);
      if (amount <= 0) return;

      const lineLabel =
        String(line?.description ?? line?.item_name ?? line?.itemName ?? "").trim() ||
        FEE_META[feeType]?.label ||
        feeType;

      const paidShare = resolveLinkedInvoicePaidShare(
        { invoice: invoicePayload },
        amount,
      );

      rows.push({
        feeType,
        lineLabel,
        amount,
        paidShare,
        invoiceNumber,
        invoiceStatus: status,
        paymentStatus: resolvePaymentStatusLabel({
          lineAmount: amount,
          linePaid: paidShare,
          invoiceStatus: status,
        }),
      });
    });
  });

  return rows;
}

/**
 * Ledger rows for Application & material fees column (commission detail).
 */
export function resolveUninvoicedInquiryFeeFlags(inquiryFeeRecord = null) {
  const appFee = Number(inquiryFeeRecord?.application_fee) || 0;
  const matFee = Number(inquiryFeeRecord?.material_fee) || 0;
  const appInv = Number(inquiryFeeRecord?.application_fee_invoiced) || 0;
  const matInv = Number(inquiryFeeRecord?.material_fee_invoiced) || 0;

  let appRem = Number(inquiryFeeRecord?.application_fee_remaining);
  if (!Number.isFinite(appRem)) {
    appRem = roundMoney(Math.max(0, appFee - appInv));
  }
  let matRem = Number(inquiryFeeRecord?.material_fee_remaining);
  if (!Number.isFinite(matRem)) {
    matRem = roundMoney(Math.max(0, matFee - matInv));
  }

  const appStatus = String(
    inquiryFeeRecord?.application_fee_status || "",
  ).toLowerCase();
  const matStatus = String(
    inquiryFeeRecord?.material_fee_status || "",
  ).toLowerCase();

  const application =
    appFee > 0.009 &&
    (appRem > 0.009 || appStatus === "unpaid" || appStatus === "partial");
  const material =
    matFee > 0.009 &&
    (matRem > 0.009 || matStatus === "unpaid" || matStatus === "partial");

  return {
    application,
    material,
    any: application || material,
  };
}

export function buildInquiryFeeLedgerSections(
  invoices = [],
  inquiryFeeRecord = null,
  currency = "AUD",
) {
  const displayCurrency =
    inquiryFeeRecord?.currency || currency || "AUD";
  const invoiceLines = collectInquiryFeeInvoiceLines(invoices);

  return ["application_fee", "material_fee"].map((feeType) => {
    const meta = FEE_META[feeType];
    const planned = roundMoney(
      inquiryFeeRecord?.[
        feeType === "application_fee" ? "application_fee" : "material_fee"
      ] ?? 0,
    );
    const paidFromApi = roundMoney(
      inquiryFeeRecord?.[
        feeType === "application_fee"
          ? "application_fee_paid"
          : "material_fee_paid"
      ] ?? 0,
    );
    const statusFromApi =
      inquiryFeeRecord?.[
        feeType === "application_fee"
          ? "application_fee_status"
          : "material_fee_status"
      ] || "none";

    const feeLines = invoiceLines.filter((row) => row.feeType === feeType);
    const invoicedTotal = roundMoney(
      feeLines.reduce((sum, row) => sum + row.amount, 0),
    );

    if (planned <= 0 && invoicedTotal <= 0 && paidFromApi <= 0) {
      return null;
    }

    const target = planned > 0 ? planned : invoicedTotal;
    const paidTotal =
      paidFromApi > 0
        ? paidFromApi
        : roundMoney(feeLines.reduce((sum, row) => sum + row.paidShare, 0));
    const balance = roundMoney(Math.max(target - paidTotal, 0));

    const rows = [];

    if (planned > 0) {
      rows.push({
        key: `${feeType}-planned`,
        label: meta.label,
        sublabel: "Planned",
        amount: formatCurrency(planned, displayCurrency),
        type: "open",
      });
    }

    if (feeLines.length) {
      feeLines.forEach((line, index) => {
        const isPaid = line.paymentStatus === "Paid";
        const isPartial = line.paymentStatus === "Partial";
        rows.push({
          key: `${feeType}-inv-${line.invoiceNumber}-${index}`,
          label: isPaid || isPartial ? line.paymentStatus : line.lineLabel,
          sublabel: isPaid
            ? `Paid · Invoice ${line.invoiceNumber}`
            : isPartial
              ? `Partial · Invoice ${line.invoiceNumber}`
              : `Invoice ${line.invoiceNumber}`,
          amount: formatCurrency(line.amount, displayCurrency),
          status: isPaid || isPartial ? null : line.paymentStatus,
          statusTone: isPaid || isPartial ? null : statusTone(line.paymentStatus),
          type: "line",
        });
      });
    } else if (planned > 0) {
      rows.push({
        key: `${feeType}-not-invoiced`,
        label: meta.label,
        sublabel: "Not on invoice yet",
        amount: "—",
        status: statusFromApi === "paid" ? "Paid" : "Unpaid",
        statusTone: statusFromApi === "paid" ? "green" : "slate",
        type: "line",
      });
    }

    if (paidTotal > 0) {
      rows.push({
        key: `${feeType}-paid`,
        label: "Less: Paid",
        amount: formatCurrency(paidTotal, displayCurrency),
        type: "deduct",
      });
    }

    rows.push({
      key: `${feeType}-balance`,
      label: `${meta.label} balance`,
      amount: formatCurrency(balance, displayCurrency),
      type: "total",
      highlight: true,
    });

    return {
      id: feeType,
      title: meta.label,
      rows,
    };
  }).filter(Boolean);
}
